import { useEffect, useState, useRef } from "react";
import { Form } from "antd";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { getSocieties, mergeSociety, getAffectedUsersCount, getDarkStores } from "../store/admin-slice/adminServicesSlice";
import { Society } from "common/interface/Society";
import { debounce } from "common/utils";

export const useMergeSociety = () => {
    const dispatch = useAppDispatch();
    const { societies, status, error, darkStores } = useAppSelector((state) => state.adminServices);
    const [selectedStoreId, setSelectedStoreId] = useState<string | undefined>();
    const [childSocietyId, setChildSocietyId] = useState<string | undefined>();
    const [parentSocietyId, setParentSocietyId] = useState<string | undefined>();
    const [form] = Form.useForm();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Loading states for selectors
    const [isLoadingChildSocieties, setIsLoadingChildSocieties] = useState(false);
    const [isLoadingParentSocieties, setIsLoadingParentSocieties] = useState(false);
    const [childSocietiesOptions, setChildSocietiesOptions] = useState<Society[]>([]);
    const [parentSocietiesOptions, setParentSocietiesOptions] = useState<Society[]>([]);

    // Address count states
    const [childAddressCount, setChildAddressCount] = useState<number | undefined>();
    const [parentAddressCount, setParentAddressCount] = useState<number | undefined>();
    const [isLoadingChildAddressCount, setIsLoadingChildAddressCount] = useState(false);
    const [isLoadingParentAddressCount, setIsLoadingParentAddressCount] = useState(false);

    // Refs for debounced functions
    const debouncedChildSearchRef = useRef<any>(null);
    const debouncedParentSearchRef = useRef<any>(null);

    // Load dark stores on mount
    useEffect(() => {
        dispatch(getDarkStores(undefined));
    }, [dispatch]);

    // Load societies when store is selected
    useEffect(() => {
        if (selectedStoreId) {
            setIsLoadingChildSocieties(true);
            setIsLoadingParentSocieties(true);
            const filter = `storeId=${selectedStoreId}`;
            dispatch(getSocieties({ pageNo: 1, pageSize: 100, filter }))
                .unwrap()
                .then((data) => {
                    const societiesList = data.data || [];
                    console.log("Societies loaded:", societiesList.length, "items");
                    setChildSocietiesOptions(societiesList);
                    setParentSocietiesOptions(societiesList);
                    setIsLoadingChildSocieties(false);
                    setIsLoadingParentSocieties(false);
                })
                .catch((err) => {
                    console.error("Error loading societies:", err);
                    setIsLoadingChildSocieties(false);
                    setIsLoadingParentSocieties(false);
                });
        } else {
            setChildSocietiesOptions([]);
            setParentSocietiesOptions([]);
            setChildSocietyId(undefined);
            setParentSocietyId(undefined);
        }
    }, [selectedStoreId, dispatch]);

    // Initialize debounced search functions
    useEffect(() => {
        debouncedChildSearchRef.current = debounce((query: string) => {
            // If query is empty, show all initial societies
            if (!query.trim()) {
                setChildSocietiesOptions(societies.data || []);
                setIsLoadingChildSocieties(false);
                return;
            }

            // Otherwise fetch search results
            setIsLoadingChildSocieties(true);
            console.log("Searching child societies with query:", query);
            const filterStr = selectedStoreId ? `query=${query}&storeId=${selectedStoreId}` : `query=${query}`;
            dispatch(getSocieties({ pageNo: 1, pageSize: 50, filter: filterStr }))
                .unwrap()
                .then((data) => {
                    console.log("Child search results:", data.data?.length || 0);
                    setChildSocietiesOptions(data.data || []);
                    setIsLoadingChildSocieties(false);
                })
                .catch((err) => {
                    console.error("Child search error:", err);
                    setChildSocietiesOptions(societies.data || []);
                    setIsLoadingChildSocieties(false);
                });
        }, 400);

        debouncedParentSearchRef.current = debounce((query: string) => {
            // If query is empty, show all initial societies
            if (!query.trim()) {
                setParentSocietiesOptions(societies.data || []);
                setIsLoadingParentSocieties(false);
                return;
            }

            // Otherwise fetch search results
            setIsLoadingParentSocieties(true);
            console.log("Searching parent societies with query:", query);
            const filterStr = selectedStoreId ? `query=${query}&storeId=${selectedStoreId}` : `query=${query}`;
            dispatch(getSocieties({ pageNo: 1, pageSize: 50, filter: filterStr }))
                .unwrap()
                .then((data) => {
                    console.log("Parent search results:", data.data?.length || 0);
                    setParentSocietiesOptions(data.data || []);
                    setIsLoadingParentSocieties(false);
                })
                .catch((err) => {
                    console.error("Parent search error:", err);
                    setParentSocietiesOptions(societies.data || []);
                    setIsLoadingParentSocieties(false);
                });
        }, 400);
    }, [dispatch, societies.data, selectedStoreId]);

    // Handle child society search
    const handleChildSocietySearch = (query: string) => {
        debouncedChildSearchRef.current(query);
    };

    // Handle parent society search
    const handleParentSocietySearch = (query: string) => {
        debouncedParentSearchRef.current(query);
    };

    // Handle child society selection
    const handleChildSocietyChange = (value: string) => {
        setChildSocietyId(value);
        form.setFieldsValue({ parentSociety: undefined });
        setParentSocietyId(undefined);
        setParentAddressCount(undefined);

        // Fetch address count for child society
        if (value) {
            setIsLoadingChildAddressCount(true);
            dispatch(getAffectedUsersCount({ societyId: value }))
                .unwrap()
                .then((data) => {
                    setChildAddressCount(data.count);
                    setIsLoadingChildAddressCount(false);
                })
                .catch((err) => {
                    setIsLoadingChildAddressCount(false);
                    console.error("Error fetching child society address count:", err);
                });
        } else {
            setChildAddressCount(undefined);
        }
    };

    // Handle parent society selection
    const handleParentSocietyChange = (value: string) => {
        setParentSocietyId(value);

        // Fetch address count for parent society
        if (value) {
            setIsLoadingParentAddressCount(true);
            dispatch(getAffectedUsersCount({ societyId: value }))
                .unwrap()
                .then((data) => {
                    setParentAddressCount(data.count);
                    setIsLoadingParentAddressCount(false);
                })
                .catch((err) => {
                    setIsLoadingParentAddressCount(false);
                    console.error("Error fetching parent society address count:", err);
                });
        } else {
            setParentAddressCount(undefined);
        }
    };

    // Handle merge button click
    const handleMerge = async () => {
        try {
            await form.validateFields();
            if (childSocietyId && parentSocietyId) {
                // If affected addresses count is 0, directly merge without showing dialog
                if (childAddressCount === 0) {
                    performMerge();
                } else {
                    // Show confirmation dialog if affected addresses > 0
                    setShowConfirmDialog(true);
                }
            }
        } catch (error) {
            console.log("Validation failed:", error);
        }
    };

    // Perform the merge operation
    const performMerge = () => {
        if (childSocietyId && parentSocietyId) {
            dispatch(mergeSociety({
                targetSocietyId: parentSocietyId,
                sourceSocietyId: childSocietyId
            }));
            setShowConfirmDialog(false);
        }
    };

    // Handle confirmation dialog cancel
    const handleConfirmDialogCancel = () => {
        setShowConfirmDialog(false);
    };

    // Calculate if merge button should be disabled
    // Fix for Issue #1: Disable button while status === "loading" (any Redux operation in progress)
    const isMergeDisabled =
        !childSocietyId ||
        !parentSocietyId ||
        status === "loading" ||
        status === "saving" ||
        isLoadingChildAddressCount;

    return {
        // Redux state
        darkStores,
        societies,
        status,
        error,
        // Local state
        selectedStoreId,
        childSocietyId,
        parentSocietyId,
        form,
        showConfirmDialog,
        isLoadingChildSocieties,
        isLoadingParentSocieties,
        childSocietiesOptions,
        parentSocietiesOptions,
        childAddressCount,
        parentAddressCount,
        isLoadingChildAddressCount,
        isLoadingParentAddressCount,
        // Derived state (Issue #1 fix)
        isMergeDisabled,
        // Handlers
        setSelectedStoreId,
        handleChildSocietySearch,
        handleParentSocietySearch,
        handleChildSocietyChange,
        handleParentSocietyChange,
        handleMerge,
        performMerge,
        handleConfirmDialogCancel,
    };
};
